// src/pages/markets/board/state/useMutedTopics.ts
//
// The user's muted tags and categories, at
// users/{uid}/preferences/muted_topics — the same per-user preference space the
// board settings live in, and already covered by the preferences rule.
//
// Read by the board (to exclude them from every column), by the filter panel
// (to grey the chips) and by search. Written only from the settings page.

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetIdentity } from 'react-admin';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { EMPTY_MUTED, knownMuted, normalizeMuted, type MutedTopics } from './mutedTopics';
import { useSchema } from '../api/useSchema';

const PREF_DOC_PATH = ['preferences', 'muted_topics'] as const;

export function mutedTopicsKey(userId: string | null) {
    return ['preferences', 'muted_topics', userId ?? ''] as const;
}

export interface UseMutedTopicsResult {
    /** As stored — what Settings edits. May contain values the schema no longer offers. */
    muted: MutedTopics;
    /** Guarded against the live vocabulary — what every query must send. */
    effective: MutedTopics;
    isLoaded: boolean;
    save: (next: MutedTopics) => Promise<void>;
}

export function useMutedTopics(): UseMutedTopicsResult {
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const userId = identity?.id ? String(identity.id) : null;
    const qc = useQueryClient();
    const queryKey = mutedTopicsKey(userId);

    const query = useQuery<MutedTopics>({
        queryKey,
        queryFn: async () => {
            const ref = doc(getFirestore(), 'users', userId as string, ...PREF_DOC_PATH);
            const snap = await getDoc(ref);
            return snap.exists() ? normalizeMuted(snap.data()) : EMPTY_MUTED;
        },
        enabled: Boolean(userId),
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // A failed read must not mute anything — the board stays wide rather than silently hiding events the user never asked to hide.
    const muted = query.data ?? EMPTY_MUTED;

    // A user with nothing muted needs no vocabulary, so they never pay for the
    // board schema — this hook runs on every page through the header's search.
    //
    // The cost is deliberate and one-sided: hasMutes is false until the
    // preferences read resolves, so for a user who DOES have mutes the schema
    // fetch is chained behind it rather than racing it. Off the board, on a cold
    // cache, that widens the fail-open window from one round trip to two — a
    // search issued in between goes out unmuted. Fetching in parallel would mean
    // fetching for everyone on every page, which is the cost this gate exists to
    // remove. On /markets the chain does not arise: ScreenerBoardView asks for
    // the schema unconditionally on the same query key.
    const hasMutes = muted.tags.length > 0 || muted.categories.length > 0;
    const { data: schema } = useSchema({ enabled: hasMutes });
    const effective = useMemo(() => knownMuted(muted, schema), [muted, schema]);

    // Not debounced, unlike the board's filter writes: this one is saved from a
    // settings form on an explicit action, not on every keystroke.
    // Assumes callers serialise: overlapping saves would each roll back to their
    // own snapshot, so a late failure could clobber a later success.
    const save = useCallback(
        async (next: MutedTopics) => {
            if (!userId) return;
            const previous = qc.getQueryData<MutedTopics>(queryKey) ?? EMPTY_MUTED;
            qc.setQueryData<MutedTopics>(queryKey, next);
            const ref = doc(getFirestore(), 'users', userId, ...PREF_DOC_PATH);
            try {
                await setDoc(
                    ref,
                    { tags: next.tags, categories: next.categories },
                    { merge: true },
                );
            } catch (err) {
                // Roll back, or the optimistic write leaves a failed save looking saved.
                qc.setQueryData<MutedTopics>(queryKey, previous);
                throw err;
            }
        },
        [qc, queryKey, userId],
    );

    return {
        muted,
        effective,
        isLoaded: !userId ? !identityLoading : query.isSuccess || query.isError,
        save,
    };
}
